import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CalendarCheck, CheckCircle } from 'lucide-react';
import {
  CreatePaymentDto,
  PaymentMethod,
  PaymentPlanNoticeDto,
  BALANCE_TOLERANCE,
  getPaymentMethodName,
  paymentsApi,
  handleApiError,
  formatCurrency,
  formatDate
} from '../../services/paymentsService';

/** התוכנית הפעילה של הלקוח — לשלב ההחלטה אם התשלום שייך לתוכנית או מחוץ לה. */
export interface ActivePlanInfo {
  id: number;
  nextInstallmentNumber?: number; // התשלום הראשון שטרם שולם
  nextInstallmentAmount?: number;
  nextDueDate?: string;
}

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (planNotice?: PaymentPlanNoticeDto | null) => void;
  clientId: number;
  clientName: string;
  activePlan?: ActivePlanInfo | null;
  balanceAfterDiscount?: number; // יתרה לתשלום לאחר הנחה — לזיהוי תשלום שמכסה את הכל
}

type ViewState = 'form' | 'planChoice' | 'initiating' | 'iframe' | 'success' | 'error';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 60; // 60 × 2s = 120s timeout

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div<{ $wide?: boolean }>`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: ${props => props.$wide ? '800px' : '600px'};
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ClientBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    if (props.variant === 'secondary') return 'var(--border-color)';
    if (props.variant === 'danger') return 'var(--error-color)';
    return 'var(--primary-color)';
  }};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IframeContainer = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 700px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  background: white;
`;

const StatusBox = styled.div<{ $variant: 'info' | 'success' | 'error' }>`
  padding: 32px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  background: ${props => {
    if (props.$variant === 'success') return 'rgba(34, 197, 94, 0.08)';
    if (props.$variant === 'error') return 'rgba(220, 38, 38, 0.08)';
    return 'rgba(59, 130, 246, 0.08)';
  }};
  color: ${props => {
    if (props.$variant === 'success') return '#15803D';
    if (props.$variant === 'error') return '#B91C1C';
    return 'var(--text-primary)';
  }};
  border-radius: var(--border-radius-md);
  margin: 24px;
`;

const StatusTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const StatusMessage = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ----- שלב ההחלטה: תשלום מתוך התוכנית / מחוץ לה -----

const ChoiceIntro = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
`;

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChoiceCard = styled.button<{ $tone: 'primary' | 'danger' }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  text-align: right;
  font-family: inherit;
  background: white;
  border: 1px solid var(--border-color);
  border-inline-start: 4px solid ${props => props.$tone === 'danger' ? 'var(--error-color)' : 'var(--primary-color)'};
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${props => props.$tone === 'danger' ? 'var(--error-color)' : 'var(--primary-color)'};
  }

  &:hover:not(:disabled) {
    background: ${props => props.$tone === 'danger' ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ChoiceText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChoiceTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ChoiceDescription = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const FullPaymentBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: var(--border-radius-md);

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #15803D;
  }
`;

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  clientName,
  activePlan,
  balanceAfterDiscount
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreatePaymentDto>({
    clientId: clientId,
    paymentDate: new Date().toISOString().split('T')[0],
    amount: 0,
    paymentMethod: PaymentMethod.Cash,
    description: '',
    referenceNumber: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [view, setView] = useState<ViewState>('form');
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const pollingRef = useRef<number | null>(null);
  const pollCountRef = useRef(0);

  const stopPolling = () => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
  };

  const resetState = () => {
    stopPolling();
    setFormData({
      clientId: clientId,
      paymentDate: new Date().toISOString().split('T')[0],
      amount: 0,
      paymentMethod: PaymentMethod.Cash,
      description: '',
      referenceNumber: ''
    });
    setErrors({});
    setView('form');
    setIframeUrl('');
    setPaymentId(null);
    setErrorMsg('');
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    } else {
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId]);

  // Polling effect for one-time charge status
  useEffect(() => {
    if (view !== 'iframe' || paymentId === null) return;

    pollCountRef.current = 0;
    pollingRef.current = window.setInterval(async () => {
      pollCountRef.current++;
      try {
        const result = await paymentsApi.getOneTimeChargeStatus(paymentId);
        if (result.status === 'Completed') {
          stopPolling();
          setView('success');
          toast.success('שולם בהצלחה');
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
          return;
        }
        if (result.status === 'Failed' || result.status === 'Cancelled') {
          stopPolling();
          setErrorMsg(result.failureReason ?? 'החיוב נכשל');
          setView('error');
          return;
        }
      } catch {
        // continue polling silently on network glitches
      }
      if (pollCountRef.current >= MAX_POLLS) {
        stopPolling();
        setErrorMsg('זמן ההמתנה לאישור הסליקה הסתיים. נא לבדוק את הסטטוס בהמשך.');
        setView('error');
      }
    }, POLL_INTERVAL_MS);

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, paymentId]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.paymentDate) {
      newErrors.paymentDate = 'יש להזין תאריך תשלום';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'יש להזין סכום תקין (גדול מאפס)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isCreditCard = formData.paymentMethod === PaymentMethod.CreditCard;
  // תשלום שמכסה את כל היתרה סוגר את התוכנית כ"הושלמה" — אין צורך לבחור
  const coversFullBalance = balanceAfterDiscount !== undefined
    && formData.amount >= balanceAfterDiscount - BALANCE_TOLERANCE;
  // שיוך לתשלום הבא בתוכנית — לא בחיוב חד-פעמי בכרטיס, שאין לו קישור לתוכנית
  const canLinkToPlan = !!activePlan?.nextInstallmentNumber && !isCreditCard;
  const nextInstallmentDetails = [
    activePlan?.nextInstallmentAmount !== undefined ? formatCurrency(activePlan.nextInstallmentAmount) : null,
    activePlan?.nextDueDate ? `מועד ${formatDate(activePlan.nextDueDate)}` : null
  ].filter(Boolean).join(', ');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // ללקוח יש תוכנית פעילה: תשלום מחוץ לה סוגר אותה — המשתמש מחליט לפני השמירה
    if (activePlan) {
      setView('planChoice');
      return;
    }

    await submitPayment(false);
  };

  const submitPayment = async (linkToPlan: boolean) => {
    // Credit card → open Cardcom iFrame instead of immediate save
    if (formData.paymentMethod === PaymentMethod.CreditCard) {
      setLoading(true);
      setView('initiating');
      try {
        const result = await paymentsApi.initiateOneTimeCharge({
          clientId,
          amount: formData.amount,
          description: formData.description || undefined
        });
        setPaymentId(result.paymentId);
        setIframeUrl(result.iframeUrl);
        setView('iframe');
      } catch (err) {
        setErrorMsg(handleApiError(err));
        setView('error');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Manual payment methods
    setLoading(true);
    try {
      const created = await paymentsApi.create({
        ...formData,
        // השרת משייך לתשלום הוותיק ביותר שטרם שולם בתוכנית
        paymentPlanId: linkToPlan && activePlan ? activePlan.id : undefined
      });
      onSuccess(created.planNotice);
      onClose();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelIframe = () => {
    const confirmed = window.confirm('האם לבטל את הסליקה? אם הלקוח כבר הזין פרטי כרטיס - החיוב עדיין עשוי לעבור.');
    if (!confirmed) return;
    stopPolling();
    resetState();
  };

  const handleRetry = () => {
    stopPolling();
    setErrorMsg('');
    setIframeUrl('');
    setPaymentId(null);
    setView('form');
  };

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent $wide={view === 'iframe'} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>רישום תשלום</ModalTitle>
            <ClientBadge>{clientName}</ClientBadge>
          </div>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {view === 'form' && (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  תאריך תשלום
                </Label>
                <Input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  hasError={!!errors.paymentDate}
                  required
                />
                {errors.paymentDate && <ErrorMessage>{errors.paymentDate}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  סכום (₪)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  hasError={!!errors.amount}
                  required
                />
                {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  <RequiredStar>*</RequiredStar>
                  אמצעי תשלום
                </Label>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: parseInt(e.target.value) })}
                  required
                >
                  <option value={PaymentMethod.Cash}>{getPaymentMethodName(PaymentMethod.Cash)}</option>
                  <option value={PaymentMethod.BankTransfer}>{getPaymentMethodName(PaymentMethod.BankTransfer)}</option>
                  <option value={PaymentMethod.Check}>{getPaymentMethodName(PaymentMethod.Check)}</option>
                  <option value={PaymentMethod.CreditCard}>{getPaymentMethodName(PaymentMethod.CreditCard)}</option>
                  <option value={PaymentMethod.DirectDebit}>{getPaymentMethodName(PaymentMethod.DirectDebit)}</option>
                  <option value={PaymentMethod.Other}>{getPaymentMethodName(PaymentMethod.Other)}</option>
                </Select>
              </FormGroup>

              {formData.paymentMethod !== PaymentMethod.CreditCard && (
                <FormGroup>
                  <Label>מספר אסמכתא</Label>
                  <Input
                    type="text"
                    value={formData.referenceNumber || ''}
                    onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                    placeholder="מספר צ'ק / אסמכתא להעברה"
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>הערות</Label>
                <TextArea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={formData.paymentMethod === PaymentMethod.CreditCard
                    ? 'תיאור החיוב (יופיע בקבלה ללקוח)'
                    : 'הערות נוספות לגבי התשלום...'}
                />
              </FormGroup>
            </ModalBody>

            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClose}>
                ביטול
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? 'שומר...'
                  : formData.paymentMethod === PaymentMethod.CreditCard
                    ? 'המשך לסליקה'
                    : 'שמירה'}
              </Button>
            </ButtonContainer>
          </form>
        )}

        {view === 'planChoice' && activePlan && (
          <>
            <ModalBody>
              {coversFullBalance ? (
                <FullPaymentBox>
                  <CheckCircle size={20} />
                  <ChoiceText>
                    <ChoiceTitle>הסכום מכסה את מלוא היתרה</ChoiceTitle>
                    <ChoiceDescription>
                      תוכנית התשלומים תיסגר כ"הושלמה" והתשלומים העתידיים בה יבוטלו.
                    </ChoiceDescription>
                  </ChoiceText>
                </FullPaymentBox>
              ) : (
                <>
                  <ChoiceIntro>
                    ללקוח קיימת תוכנית תשלומים פעילה. איך לרשום את התשלום בסך {formatCurrency(formData.amount)}?
                  </ChoiceIntro>
                  <ChoiceList>
                    {canLinkToPlan && (
                      <ChoiceCard
                        type="button"
                        $tone="primary"
                        disabled={loading}
                        onClick={() => submitPayment(true)}
                      >
                        <CalendarCheck size={20} />
                        <ChoiceText>
                          <ChoiceTitle>תשלום חודשי מתוך התוכנית</ChoiceTitle>
                          <ChoiceDescription>
                            {`יירשם כתשלום #${activePlan.nextInstallmentNumber}`}
                            {nextInstallmentDetails && ` (${nextInstallmentDetails})`}
                            {'. התוכנית ממשיכה כרגיל.'}
                          </ChoiceDescription>
                        </ChoiceText>
                      </ChoiceCard>
                    )}
                    <ChoiceCard
                      type="button"
                      $tone="danger"
                      disabled={loading}
                      onClick={() => submitPayment(false)}
                    >
                      <AlertTriangle size={20} />
                      <ChoiceText>
                        <ChoiceTitle>תשלום מראש מחוץ לתוכנית</ChoiceTitle>
                        <ChoiceDescription>
                          התוכנית הקיימת תבוטל, ויהיה צורך ליצור תוכנית חדשה ליתרה.
                        </ChoiceDescription>
                      </ChoiceText>
                    </ChoiceCard>
                  </ChoiceList>
                </>
              )}
            </ModalBody>

            <ButtonContainer>
              <Button type="button" variant="secondary" disabled={loading} onClick={() => setView('form')}>
                חזרה
              </Button>
              {coversFullBalance && (
                <Button type="button" disabled={loading} onClick={() => submitPayment(false)}>
                  {loading
                    ? 'שומר...'
                    : isCreditCard ? 'אישור והמשך לסליקה' : 'אישור ורישום'}
                </Button>
              )}
            </ButtonContainer>
          </>
        )}

        {view === 'initiating' && (
          <StatusBox $variant="info">
            <Spinner />
            <StatusTitle>מכין את דף הסליקה...</StatusTitle>
            <StatusMessage>נא להמתין מספר שניות</StatusMessage>
          </StatusBox>
        )}

        {view === 'iframe' && iframeUrl && (
          <IframeContainer>
            <Iframe
              src={iframeUrl}
              title="דף סליקה מאובטח"
              allow="payment"
            />
            <ButtonContainer style={{ padding: 0, border: 'none' }}>
              <Button type="button" variant="secondary" onClick={handleCancelIframe}>
                ביטול
              </Button>
            </ButtonContainer>
          </IframeContainer>
        )}

        {view === 'success' && (
          <StatusBox $variant="success">
            <StatusTitle>שולם בהצלחה!</StatusTitle>
            <StatusMessage>החיוב התקבל. קבלה תישלח ללקוח במייל.</StatusMessage>
          </StatusBox>
        )}

        {view === 'error' && (
          <>
            <StatusBox $variant="error">
              <StatusTitle>החיוב נכשל</StatusTitle>
              <StatusMessage>{errorMsg}</StatusMessage>
            </StatusBox>
            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClose}>
                סגירה
              </Button>
              <Button type="button" onClick={handleRetry}>
                נסה שוב
              </Button>
            </ButtonContainer>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default RecordPaymentModal;
